import { ColDef, GridApi, GridOptions, themeAlpine } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { DateTime } from "luxon";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useImmer } from "use-immer";

import { IgAzurePrimitives, IgIconButton } from "@intelligems/ig-theme";
import { Grid, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { DataType } from "@src/features/analytics/types";
import { useSelector } from "@src/store";
import { UseQueryResult } from "@tanstack/react-query";

import {
  metricsConfig,
  SitewideTableConfig,
  SitewideTableRow,
} from "./profitTableConfig";

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .ag-header-row`]: {
    backgroundColor: IgAzurePrimitives[100],
  },
  [`& .ag-group-value`]: {
    fontSize: "14px",
    lineHeight: "16.8px",
  },
  "& .ag-header-cell .ag-header-cell-label": {
    justifyContent: "flex-end",
  },
  [`& .ag-header-cell-text`]: {
    lineHeight: "16.8px",
  },
  [`& .ag-icon`]: {
    display: "none",
  },

  [`& .ag-cell-wrapper.ag-row-group`]: {
    alignItems: "center !important",
  },
  [`& .ag-group-child-count`]: {
    display: "none",
  },
}));

interface ProfitAndLossTableProps {
  sitewideAnalyticsDataQuery: UseQueryResult<any>;
}

export const SitewideAnalyticsProfitTable = ({
  sitewideAnalyticsDataQuery,
}: ProfitAndLossTableProps) => {
  const graphValueFormatter = useGraphValueFormatter();
  const theme = useTheme();
  const gridRef = useRef<AgGridReact>(null);
  const sitewideDashboardFilters = useSelector(
    (state) => state.sitewideDashboardFilters
  );

  const [gridApi, setGridApi] = useImmer<GridApi | undefined>(undefined);
  const [clickEvent, setClickEvent] = useImmer<
    { cellClicked: string; index: number | null } | undefined
  >(undefined);
  const [collapsibleRows, setCollapsibleRows] = useImmer<
    Map<string, { rows: any[]; renderRows: boolean }> | undefined
  >(undefined);

  const myTheme = themeAlpine.withParams({
    fontFamily: theme.typography.fontFamily,
    rowBorder: `1px solid ${theme.igBorders.core.default}`,
    headerRowBorder: `1px solid ${theme.igBorders.core.default}`,
    headerFontWeight: 600,
    headerTextColor: theme.palette.text.secondary,
    headerFontSize: "14px",
    wrapperBorder: `1px solid ${theme.igBorders.core.default}`,
    wrapperBorderRadius: "8px",
    borderRadius: "8px",
    columnBorder: `1px solid ${theme.igBorders.core.default}`,
  });

  const gridOptions = useMemo<GridOptions>(() => {
    return {
      domLayout: "autoHeight",
      tooltipInteraction: true,
      headerHeight: 58,
      groupDefaultExpanded: -1,
      suppressAggFuncInHeader: true,
      groupDisplayType: "groupRows",
      groupRowRenderer: "agGroupCellRenderer",
      animateRows: true,
      onCellClicked: (event) => {
        // registers a click event on the cell to manage collapsible rows
        setClickEvent({ cellClicked: event.data.key, index: event.rowIndex });
      },
    };
  }, []);
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      resizable: false,
      cellStyle: {
        fontSize: "14px",
        alignContent: "center",
        textAlign: "right",
      },
      menuTabs: [],
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      sortable: false,
    };
  }, []);

  const getFormattedDate = (date: string) => {
    if (sitewideDashboardFilters.interval.includes("1 MONTH")) {
      const dateTime = new Date(date);
      const formattedDateTime = dateTime.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
      return formattedDateTime;
    }
    return sitewideDashboardFilters.interval.includes("DAY")
      ? DateTime.fromSQL(date).toFormat("MM/dd/yyyy")
      : DateTime.fromISO(date).toFormat("MM/dd/yyyy");
  };

  const profitData = useMemo(() => {
    if (sitewideAnalyticsDataQuery && sitewideAnalyticsDataQuery?.data?.data) {
      return sitewideAnalyticsDataQuery.data.data as any[];
    }
    return [];
  }, [sitewideAnalyticsDataQuery]);

  const columnDefs = useMemo(() => {
    if (!profitData || profitData.length === 0) return [];

    // Get all unique dates from the data
    const dates = new Set();
    profitData.forEach((item) => {
      if (item.dt) dates.add(item.dt);
    });

    const sortedDates = Array.from(dates).sort();

    // Create column definitions
    const cols: ColDef[] = [
      {
        field: "metric",
        headerName: "",
        pinned: "left",
        width: 230,
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (params) => {
          // manages showing row labels or row labels with chevron's depending on collapsible state of group
          if (
            params.data?.dropDownGroup?.type === "container" &&
            collapsibleRows
          ) {
            return (
              <Grid
                container
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Grid item>{params.value}</Grid>
                <Grid item>
                  <IgIconButton sx={{ paddingLeft: "4" }}>
                    {collapsibleRows[params.data.key].renderRows ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )}
                  </IgIconButton>
                </Grid>
              </Grid>
            );
          }

          return params.value;
        },
        cellStyle: (params) => {
          let baseStyle: any = {
            fontWeight: 400,
            backgroundColor:
              theme.igBackgroundColors.interactive.neutral.default,
            textAlign: "left",
          };

          // manages dynamic styling of collapsible section row labels
          if (params.data?.dropDownGroup?.type === "item" && collapsibleRows) {
            const rows = collapsibleRows[params.data.dropDownGroup.name].rows;
            const lastRowKey = rows[rows.length - 1];
            if (lastRowKey.key !== params.data.key) {
              baseStyle = {
                ...baseStyle,
                borderBottom: `1px solid ${theme.igBorders.core.default}`,
              };
            }
          }

          if (params.data?.rowStyle === "bold") {
            baseStyle = {
              ...baseStyle,
              fontWeight: 600,
              backgroundColor:
                theme.igBackgroundColors.interactive.neutral.focused,
            };
          }

          return baseStyle;
        },
      },
    ];

    sortedDates.forEach((date: string) => {
      cols.push({
        field: date,
        headerName: getFormattedDate(date),
        width: 120,
        minWidth: 120,
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined) return "";
          if (typeof params.value === "number") {
            return params.value.toLocaleString();
          }
          return params.value;
        },
      });
    });

    return cols;
  }, [profitData, collapsibleRows, clickEvent]);

  const getRowStyle = (params) => {
    let style = {
      background: "none",
      borderBottom: `1px solid ${theme.igBorders.core.default}`,
    };
    if (params.node.data?.rowStyle === "bold") {
      style.background = theme.igBackgroundColors.interactive.neutral.default;
    }
    // manages dynamic styling of collapsible section row cells
    if (params.data?.dropDownGroup?.type === "item" && collapsibleRows) {
      const rows = collapsibleRows[params.data.dropDownGroup.name].rows;
      const lastRowKey = rows[rows.length - 1];
      if (lastRowKey.key !== params.data.key) {
        style = { ...style, borderBottom: "none" };
      }
    }

    return style;
  };

  const getFormattedMetricValue = useCallback(
    (value: number, metricDataType: DataType) => {
      if (!value) {
        return "-";
      }
      return graphValueFormatter({
        isAxisTick: false,
        value: value || 0,
        configDataTypes: [metricDataType],
      });
    },
    [profitData]
  );

  // Transform data into row format
  const rowData = useMemo(() => {
    if (!profitData || profitData.length === 0) return [];

    // Get all unique dates
    const dates = [...new Set(profitData.map((item) => item.dt))].sort();

    // Create a lookup for data by date
    const dataByDate = {};
    const renderCollapsibleConfig: Map<
      string,
      { rows: any[]; renderRows: boolean }
    > = new Map();
    const renderIndex = 0;
    profitData.forEach((item) => {
      dataByDate[item.dt] = item;
    });
    // Build rows

    const rows = metricsConfig
      .map((metric: SitewideTableConfig) => {
        // Pulls data from config and enriches it with profit table data by date to create table rows
        const row: SitewideTableRow = {
          metric: metric.label,
          rowStyle: metric.rowStyle,
          key: metric.key,
          showCollapsibleSection: metric.showCollapsibleSection,
          excludeFromTable: metric.excludeFromTable,
          dropDownGroup: metric.dropDownGroup,
        };
        dates.forEach((date) => {
          const dateData = dataByDate[date];
          const value = getFormattedMetricValue(
            dateData[metric.key],
            metric.format
          );
          row[date] = value;
        });
        return row;
      })
      .filter((row) => {
        // Filters out metrics excluded from table based on return value of excluedFromTable functions
        // creates the collapsibleRows object that is used to manage the state of the table
        // filters out initially un-rendered rows from being displayed (metrics with showCollapsibleSection that return false)
        if (
          row.excludeFromTable &&
          !row.excludeFromTable({
            row,
            dates,
            dataByDate,
            sitewideDashboardFilters,
          })
        ) {
          return false;
        }

        if (row.showCollapsibleSection) {
          const renderRow = row.showCollapsibleSection({
            row: row,
            dates: dates,
            dataByDate: dataByDate,
          });

          if (row.dropDownGroup && row.dropDownGroup.type === "item") {
            if (renderCollapsibleConfig[row.dropDownGroup.name]) {
              renderCollapsibleConfig[row.dropDownGroup.name].rows.push(row);
            } else {
              renderCollapsibleConfig[row.dropDownGroup.name] = {
                rows: [row],
                renderRows: renderRow,
              };
            }
          }

          return renderRow;
        }
        return true;
      });
    setCollapsibleRows(renderCollapsibleConfig);
    return rows;
  }, [profitData]);

  useEffect(() => {
    // Manages the hiding and showing of collapsible sections based on click events
    if (
      gridApi &&
      rowData.length > 0 &&
      collapsibleRows &&
      clickEvent?.cellClicked &&
      clickEvent?.index !== null &&
      clickEvent?.index !== undefined &&
      collapsibleRows[clickEvent?.cellClicked]
    ) {
      if (!collapsibleRows[clickEvent.cellClicked].renderRows) {
        // when there is a click event and the cell is not already expanded, expand the cells
        // using the gridApi (ensures that transition is applied correctly)
        const tempCollapsibleRows = collapsibleRows;
        // eslint-disable-next-line react-compiler/react-compiler
        tempCollapsibleRows[clickEvent.cellClicked].renderRows = true;
        setCollapsibleRows(tempCollapsibleRows);
        gridApi.applyTransaction({
          add: collapsibleRows[clickEvent.cellClicked].rows,
          addIndex: clickEvent.index + 1, // click event contains cell of the row, so we need to add before it
        });
      } else {
        // when there is a click event and the cell is already expanded, remove the cells
        // using the gridApi (ensures that transition is applied correctly)
        const tempCollapsibleRows = collapsibleRows;
        tempCollapsibleRows[clickEvent.cellClicked].renderRows = false;
        setCollapsibleRows(tempCollapsibleRows);
        gridApi.applyTransaction({
          remove: collapsibleRows[clickEvent.cellClicked].rows,
        });
      }
    }
  }, [clickEvent]);

  return (
    <StyledGrid
      container
      item
      xs={12}
      direction={"column"}
      rowSpacing={2}
      sx={{ paddingTop: 5, paddingBottom: 5, width: "100%" }}
    >
      <AgGridReact
        ref={gridRef}
        theme={myTheme}
        loading={sitewideAnalyticsDataQuery.isLoading}
        defaultColDef={defaultColDef}
        rowData={rowData}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        suppressDragLeaveHidesColumns={true}
        suppressExcelExport={true}
        suppressCsvExport={true}
        suppressContextMenu={true}
        suppressCellFocus={true}
        overlayLoadingTemplate={"<div>Loading Data... </div>"}
        overlayNoRowsTemplate={
          "<div>No Data - Your filters may have resulted in no data, try resetting them</div>"
        }
        onGridReady={(params) => setGridApi(params.api)}
        headerHeight={38}
        suppressRowHoverHighlight={true}
        getRowStyle={getRowStyle}
      />
    </StyledGrid>
  );
};
